import {
    count,
    groups,
    workflowContextProps,
    workflowSearchProps,
    workflowStepProps,
} from './order-workflow-contract'
import type {
    FieldGroup,
    FormVariant,
    WorkflowAction,
    WorkflowContext,
    WorkflowIdentity,
    WorkflowOptions,
    WorkflowOutcome,
    WorkflowSearch,
    WorkflowStep,
} from './order-workflow-contract'

export type {
    FieldGroup,
    FormVariant,
    WorkflowIdentity,
} from './order-workflow-contract'
export function createOrderWorkflowTracker(
    identity: WorkflowIdentity,
    variant: FormVariant,
    prefilled: boolean,
    options: WorkflowOptions = {},
) {
    const actor = identity.actorId
    const impersonating = identity.impersonating
    const now = options.now ?? (() => performance.now())
    const attemptId = crypto.randomUUID()
    const opened = now()
    let last = opened,
        activity = -Infinity,
        sequence = 0,
        submitIndex = 0
    let foreground = false,
        started = false,
        finished = false,
        engaged = false
    let foregroundMs = 0,
        activeMs = 0,
        waitMs = 0,
        lastCheckpoint = opened
    let currentGroup: FieldGroup | undefined
    const groupMs = { customer: 0, items: 0, shipping: 0, payment: 0 }
    const touched = new Set<FieldGroup>()
    const requests = new Map<symbol, number>()
    const records = new Set<string>()
    let context: Record<string, unknown> = {}
    const emit = (event: string, props: Record<string, unknown> = {}) => {
        try {
            options.capture?.(event, {
                schema_version: 1,
                workflow_type: 'order_create',
                attempt_id: attemptId,
                event_id: crypto.randomUUID(),
                sequence: ++sequence,
                actor,
                impersonating,
                form_variant: variant,
                route_template: '/orders/new',
                ...context,
                ...props,
            })
        } catch {
            /* Telemetry must never block a business operation. */
        }
    }
    const observe = () => {
        const time = now()
        const delta = Math.max(0, time - last)
        // Heartbeat runs each second. A missing observation cannot prove human effort.
        if (!finished && foreground && delta <= 5000) {
            foregroundMs += delta
            const active = Math.max(0, Math.min(time, activity + 30000) - last)
            activeMs += active
            if (currentGroup) groupMs[currentGroup] += active
        }
        last = time
    }
    const snapshot = () => {
        observe()
        return {
            elapsed_ms: count(now() - opened),
            foreground_ms: count(foregroundMs),
            interaction_active_ms: count(activeMs),
            api_wait_ms: count(
                waitMs +
                    [...requests.values()].reduce(
                        (sum, start) => sum + Math.max(0, now() - start),
                        0,
                    ),
            ),
            customer_active_ms: count(groupMs.customer),
            items_active_ms: count(groupMs.items),
            shipping_active_ms: count(groupMs.shipping),
            payment_active_ms: count(groupMs.payment),
            submit_index: submitIndex,
        }
    }
    const checkpoint = () => {
        if (started && !finished) {
            emit('workflow_checkpoint', snapshot())
            lastCheckpoint = now()
        }
    }
    return {
        attemptId,
        setContext(metadata: WorkflowContext) {
            if (!finished)
                context = { ...context, ...workflowContextProps(metadata) }
        },
        start() {
            if (!started) {
                started = true
                emit('workflow_started', {
                    entry_point: 'orders_new',
                    prefilled: !!prefilled,
                })
            }
        },
        observe() {
            observe()
            if (foreground && now() - lastCheckpoint >= 30000) checkpoint()
        },
        setVariant(next: FormVariant) {
            if (next === 'desktop' || next === 'mobile') variant = next
        },
        setForeground(value: boolean) {
            observe()
            foreground = value
            if (!value) checkpoint()
        },
        interact(group: FieldGroup) {
            if (finished || !groups.includes(group)) return
            observe()
            activity = now()
            if (currentGroup === group) return
            currentGroup = group
            if (!engaged) {
                engaged = true
                emit('workflow_step', {
                    action: 'engaged',
                    engaged_at_ms: count(now() - opened),
                })
            }
            emit('workflow_step', {
                action: touched.has(group)
                    ? 'field_revised'
                    : 'field_interacted',
                field_group: group,
            })
            touched.add(group)
        },
        step(action: WorkflowAction, metadata: WorkflowStep = {}) {
            const props = workflowStepProps(action, metadata, submitIndex)
            if (!props) return
            emit('workflow_step', props)
        },
        search(metadata: WorkflowSearch) {
            if (finished) return
            emit('workflow_search_results', workflowSearchProps(metadata))
        },
        submit() {
            submitIndex++
            const token = Symbol()
            requests.set(token, now())
            emit('workflow_step', {
                action: 'submit',
                submit_index: submitIndex,
            })
            return () => {
                const start = requests.get(token)
                if (start !== undefined) {
                    waitMs += Math.max(0, now() - start)
                    requests.delete(token)
                }
            }
        },
        created(orderId: string) {
            if (!orderId || records.has(orderId)) return
            records.add(orderId)
            emit('workflow_step', {
                action: 'creation_result',
                order_id: orderId,
                result_code: 'api_confirmed',
                submit_index: submitIndex,
            })
        },
        checkpoint,
        finish(outcome: WorkflowOutcome, orderId?: string) {
            if (
                finished ||
                !['api_confirmed', 'closed', 'navigated_away'].includes(
                    outcome,
                ) ||
                (outcome === 'api_confirmed' && !orderId)
            )
                return
            const props = snapshot()
            finished = true
            emit('workflow_finished', {
                outcome,
                ...(orderId ? { order_id: orderId } : {}),
                ...props,
            })
        },
    }
}
export type OrderWorkflowTracker = ReturnType<typeof createOrderWorkflowTracker>
